import styled, { keyframes } from 'styled-components/macro';
import { FocusableParent, Focusable } from 'components/navigation';
import scaleSize from 'utils/scale-size';

export const StyledBlock = styled.div`
  width: ${scaleSize(680)}px;
  margin: ${scaleSize(34)}px auto;
`;

export const StyledTitle = styled.div`
  height: ${scaleSize(72)}px;
  font-size: ${scaleSize(48)}px;
  line-height: ${scaleSize(1.5, false)};
  letter-spacing: -0.33px;
  color: ${({ theme }) => theme.defaultColor};
  text-align: center;
`;

export const StyledDescription = styled.div`
  height: ${scaleSize(96)}px;
  font-size: ${scaleSize(34)}px;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: center;
  color: ${({ theme }) => theme.iconDefaultColor};
`;

export const StyledText = styled.div`
  width: ${scaleSize(1000)}px;
  height: ${scaleSize(96)}px;
  font-size: ${scaleSize(24)}px;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: center;
  color: ${({ theme }) => theme.mainTextColor};
  margin-left: ${scaleSize(-160)}px;
  & > i {
    color: ${({ theme }) => theme.activeColor};
  }
`;

export const FocusableActionsWrapper = styled(FocusableParent)`
  margin-top: ${scaleSize(144)}px;
  text-align: center;
`;

export const StyledButtonWrapper = styled.div`
  margin-top: ${scaleSize(20)}px;
  margin-bottom: ${scaleSize(20)}px;
`;

export const StyledButtonText = styled.div`
  width: ${scaleSize(618)}px;
  height: ${scaleSize(80)}px;
`;

export const StyledInputWrapper = styled.div`
  margin-bottom: ${scaleSize(20)}px;
`;

const errorAnimation = keyframes`
    0% { transform: translate(30px); }
    20% { transform: translate(-30px); }
    40% { transform: translate(15px); }
    60% { transform: translate(-15px); }
    80% { transform: translate(8px); }
    100% {transform: translate(0px); }
`;

interface IStyledInput {
  isValid?: boolean;
}

export const StyledInput = styled(Focusable)<IStyledInput>`
  display: block;
  width: ${scaleSize(672)}px;
  height: ${scaleSize(88)}px;
  border-radius: ${scaleSize(12)}px;
  border-style: solid;
  border-width: ${scaleSize(4)}px;
  border-color: ${({ isValid, theme }) => (isValid ? 'transparent' : theme.invalidInput)};
  background-color: ${({ theme }) => theme.submenuActiveColor};
  overflow: hidden;
  animation-name: ${({ isValid }) => (isValid ? '' : errorAnimation)};
  animation-duration: 0.4s;
  animation-iteration-count: 1;

  &:before {
    content: '';
    position: absolute;
    display: block;
    width: ${scaleSize(36)}px;
    height: ${scaleSize(86)}px;
    border-radius: ${scaleSize(12)}px;
    background-color: ${({ theme }) => theme.submenuActiveColor};
  }

  &:focus {
    border-color: ${({ theme }) => theme.activeColor};
  }
`;

const cursorAnimation = keyframes`
  0% {opacity: 0;}
  50% {opacity: 1;}
  100% {opacity: 0;}
`;

export const StyledInputCursor = styled.div`
  display: inline-block;
  width: 2px;
  height: ${scaleSize(50)}px;
  vertical-align: middle;
  margin-top: -${scaleSize(4)}px;
  margin-left: -1px;
  background: ${({ theme }) => theme.mainTextColor};
  animation-name: ${cursorAnimation};
  animation-duration: 1s;
  animation-iteration-count: infinite;
`;

interface IStyledInputText {
  isActive?: boolean;
  centeredText?: boolean;
}

export const StyledInputText = styled.div<IStyledInputText>`
  height: ${scaleSize(48)}px;
  min-width: ${scaleSize(600)}px;
  float: right;
  margin: 0 ${scaleSize(36)}px;
  text-align: ${({ centeredText }) => (centeredText ? 'center' : 'left')};
  white-space: nowrap;
  font-size: ${scaleSize(40)}px;
  line-height: ${scaleSize(88)}px;
  letter-spacing: ${scaleSize(-0.28)}px;
  color: ${({ isActive, theme }) => theme[isActive ? 'defaultColor' : 'iconDefaultColor']};
`;

export const StyledInputInvalidMessage = styled.div`
  height: ${scaleSize(40)}px;
  margin-top: ${scaleSize(12)}px;
  font-size: ${scaleSize(32)}px;
  text-align: left;
  font-family: 'RobotoRegular', sans-serif;
  line-height: 1.25;
  letter-spacing: -0.22px;
  color: ${({ theme }) => theme.invalidInput};
`;

export const StyledWhiteText = styled.span`
  color: ${({ theme }) => theme.defaultColor};
`;

export const StyledKeyboardWrapper = styled.div`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(420)}px;
  background-color: ${({ theme }) => theme.imagePlaceholder};
  text-align: center;
  position: absolute;
  bottom: 0;
  left: 0;
  padding-left: ${scaleSize(20)}px;

  & > div {
    margin-top: ${scaleSize(40)}px;
    margin-bottom: ${scaleSize(40)}px;
  }
`;

export const StyledPinInputChar = styled.span<{ isActive: boolean; isPlaceholder: boolean }>`
  display: inline-block;
  width: ${scaleSize(45)}px;
  text-align: center;
  color: ${({ isActive, isPlaceholder, theme }) =>
    isActive ? theme.activeColor : isPlaceholder ? theme.iconDefaultColor : 'inherit'};
`;
